import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CircleNotch, ListBullets, Wrench, CurrencyDollar, Clock, Camera, UploadSimple } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function MaterialRow({ item }) {
  return (
    <div className="flex items-center justify-between p-2.5 border-b border-[#E5E5E5] last:border-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#0A0A0A]">{item.name}</p>
      </div>
      <div className="flex items-center gap-3 shrink-0 text-right">
        <span className="font-mono-data text-[10px] text-[#404040]">
          {item.quantity} {item.unit}
        </span>
        {item.estimated_cost > 0 && (
          <span className="font-mono-data text-[10px] text-[#0A0A0A] font-bold">
            ${item.estimated_cost}
          </span>
        )}
      </div>
    </div>
  );
}

function ToolRow({ item }) {
  return (
    <div className="flex items-center gap-2 p-2 border-b border-[#E5E5E5] last:border-0">
      <Wrench size={12} className="text-[#404040] shrink-0" />
      <span className="text-sm text-[#0A0A0A]">{item.name}</span>
      {item.required && (
        <span className="font-mono-data text-[10px] text-[#FF5A00] ml-auto">REQUIRED</span>
      )}
    </div>
  );
}

export default function MaterialListPage() {
  const [trade, setTrade] = useState("electrical");
  const [jobType, setJobType] = useState("");
  const [description, setDescription] = useState("");
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const JOB_TYPES = {
    electrical: [
      { value: "service_upgrade_200a", label: "Service Upgrade (200A)" },
      { value: "new_circuit_20a", label: "New Circuit (20A)" },
      { value: "gfci_installation", label: "GFCI Installation" },
      { value: "whole_house_rewire", label: "Whole House Rewire" },
      { value: "ceiling_fan_install", label: "Ceiling Fan Install" },
      { value: "panel_replacement", label: "Panel Replacement" },
    ],
    hvac: [
      { value: "ac_install_3ton", label: "AC Install (3 Ton)" },
      { value: "furnace_replacement", label: "Furnace Replacement" },
      { value: "duct_repair", label: "Duct Repair" },
      { value: "mini_split_install", label: "Mini Split Install" },
      { value: "thermostat_install", label: "Thermostat Install" },
      { value: "duct_cleaning", label: "Duct Cleaning" },
    ],
  };

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhoto(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const generate = async () => {
    if (!description.trim() && !jobType) {
      toast.error("Provide a job type or description");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("trade", trade);
      formData.append("job_type", jobType);
      formData.append("description", description || `${jobType.replace(/_/g, " ")} job`);
      if (photo) formData.append("photo", photo);
      const res = await axios.post(`${API}/generate-materials`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setResult(res.data);
      toast.success("Material list generated");
    } catch {
      toast.error("Failed to generate material list");
    }
    setLoading(false);
  };

  return (
    <div className="p-4 max-w-lg mx-auto" data-testid="material-list-page">
      <h1 className="text-xl font-black tracking-tight mb-1" style={{ fontFamily: "'Chivo', sans-serif" }}>
        Material List
      </h1>
      <p className="text-xs text-[#404040] mb-2 font-mono-data">AI-POWERED JOB ESTIMATOR</p>

      {/* Disclaimer */}
      <div className="p-2.5 bg-[#F4F4F5] border border-[#E5E5E5] rounded-sm mb-4">
        <p className="text-[10px] text-[#404040] leading-relaxed">
          <b>Disclaimer:</b> Material lists and cost estimates are AI-generated for <b>reference only</b>. 
          Verify all quantities and specifications before purchasing. WorkPro assumes no liability 
          for inaccurate estimates.
        </p>
      </div>

      {/* Trade & Job Type */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <Select value={trade} onValueChange={v => { setTrade(v); setJobType(""); setResult(null); }}>
          <SelectTrigger data-testid="material-trade-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="electrical">Electrical</SelectItem>
            <SelectItem value="hvac">HVAC</SelectItem>
          </SelectContent>
        </Select>
        <Select value={jobType} onValueChange={setJobType}>
          <SelectTrigger data-testid="material-jobtype-select"><SelectValue placeholder="Job Type" /></SelectTrigger>
          <SelectContent>
            {(JOB_TYPES[trade] || []).map(jt => (
              <SelectItem key={jt.value} value={jt.value}>{jt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Description */}
      <textarea
        data-testid="material-description"
        value={description}
        onChange={e => setDescription(e.target.value)}
        placeholder="Describe the job (e.g., upgrade 100A to 200A panel in older home, 150ft from meter to panel)..."
        className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7] resize-none h-20 mb-3"
      />

      {/* Optional Photo */}
      <div className="mb-4">
        <label className="text-[10px] font-mono-data text-[#404040] block mb-1">OPTIONAL: ATTACH JOB SITE PHOTO</label>
        <div className="flex gap-2 items-center">
          <label
            data-testid="material-photo-upload"
            className="touch-target px-3 py-2 text-xs font-bold border border-[#E5E5E5] rounded-sm hover:border-[#0A0A0A] transition-colors cursor-pointer flex items-center gap-1.5"
          >
            <UploadSimple size={14} /> {photo ? "Change Photo" : "Add Photo"}
            <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
          </label>
          {photoPreview && (
            <img src={photoPreview} alt="Site" className="h-10 w-14 object-cover rounded-sm border border-[#E5E5E5]" />
          )}
        </div>
      </div>

      {/* Generate Button */}
      <button
        data-testid="generate-materials-btn"
        onClick={generate}
        disabled={loading || (!description.trim() && !jobType)}
        className="w-full py-3 bg-[#34C759] text-white text-sm font-bold rounded-sm hover:bg-[#2AA04A] transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
      >
        {loading ? (
          <><CircleNotch size={16} className="animate-spin" /> Generating...</>
        ) : (
          <><ListBullets size={16} /> Generate Material & Tool List</>
        )}
      </button>

      {/* Results */}
      {result && (
        <div className="mt-4 space-y-3 animate-slide-up" data-testid="material-result">
          {/* Summary */}
          <div className="grid grid-cols-2 gap-3">
            <div className="wp-card">
              <div className="metric-label flex items-center gap-1"><CurrencyDollar size={10} /> EST. COST</div>
              <div className="metric-value text-[#0A0A0A]">
                ${result.estimated_cost?.toLocaleString() || "N/A"}
              </div>
            </div>
            <div className="wp-card">
              <div className="metric-label flex items-center gap-1"><Clock size={10} /> EST. HOURS</div>
              <div className="metric-value text-[#002FA7]">
                {result.estimated_hours || "N/A"}
              </div>
            </div>
          </div>

          {/* Materials */}
          {result.materials?.length > 0 && (
            <div>
              <h3 className="font-mono-data text-[10px] text-[#0A0A0A] mb-1">
                MATERIALS ({result.materials.length})
              </h3>
              <div className="wp-card p-0">
                {result.materials.map((m, i) => <MaterialRow key={i} item={m} />)}
              </div>
            </div>
          )}

          {/* Tools */}
          {result.tools?.length > 0 && (
            <div>
              <h3 className="font-mono-data text-[10px] text-[#0A0A0A] mb-1">
                TOOLS ({result.tools.length})
              </h3>
              <div className="wp-card p-0">
                {result.tools.map((t, i) => <ToolRow key={i} item={t} />)}
              </div>
            </div>
          )}

          {/* Notes */}
          {result.notes && (
            <div className="wp-card">
              <span className="font-mono-data text-[10px] text-[#404040]">NOTES</span>
              <p className="text-xs text-[#404040] mt-1 leading-relaxed">{result.notes}</p>
            </div>
          )}

          {/* Raw response fallback */}
          {result.raw_ai_response && (!result.materials || result.materials.length === 0) && (
            <div className="wp-card">
              <span className="font-mono-data text-[10px] text-[#404040]">AI ANALYSIS</span>
              <p className="text-xs text-[#404040] mt-1 leading-relaxed whitespace-pre-wrap">
                {result.raw_ai_response}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
